"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  LuMapPin,
  LuLoader,
  LuNetwork,
  LuInfo,
  LuUser,
  LuGlobe,
  LuCheck,
  LuChevronRight,
  LuFileText,
  LuClipboard,
  LuBuilding,
  LuPhone,
  LuSave,
  LuPlus,
} from "react-icons/lu"
import { SelectWithSearch } from "../../utils/select-with-search"
import axiosInstance from "../../libs/axios"
import { useNavigate, useParams } from "react-router-dom"
import { uploadFilesWithPresignedUrls } from "../../libs/presigned-upload"

interface Client {
  id: number
  client_name: string
  status: string
}

interface Activity {
  id: number
  activity_name: string
  status: string
}

interface Project {
  id: number
  project_name: string
  status: string
  client?: Client
  activity?: Activity
}

interface EditSiteData {
  site_id: string
  project_id: number
  activity_id?: number
  reference_number?: string
  client_allocation_date?: string
  site_name?: string
  site_address?: string
  opco_name?: string
  opco_id?: string
  state_circle_id?: number
  district_id?: number
  pincode?: string
  site_latitude?: number
  site_longitude?: number
  site_priority?: string
  pr_number?: string
  pr_date?: string
  po_number?: string
  po_date?: string
  po_files?: string[]
  is_po_no_shared_at_allocation?: boolean
  technician_name?: string
  technician_number?: string
  fse_name?: string
  fse_number?: string
  aom_name?: string
  aom_number?: string
  client_project_name?: string
  customer_requirement?: string
  site_information1?: string
  site_information2?: string
  site_information3?: string
  contact_person?: string
  contact_number?: string
}

const EditSite = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState<EditSiteData>({
    site_id: "",
    project_id: 0,
    activity_id: undefined,
    reference_number: "",
    client_allocation_date: "",
    site_name: "",
    site_address: "",
    opco_name: "",
    opco_id: "",
    state_circle_id: undefined,
    district_id: undefined,
    pincode: "",
    site_latitude: undefined,
    site_longitude: undefined,
    site_priority: "medium",
    pr_number: "",
    pr_date: "",
    po_number: "",
    po_date: "",
    po_files: [],
    is_po_no_shared_at_allocation: false,
    technician_name: "",
    technician_number: "",
    fse_name: "",
    fse_number: "",
    aom_name: "",
    aom_number: "",
    client_project_name: "",
    customer_requirement: "",
    site_information1: "",
    site_information2: "",
    site_information3: "",
    contact_person: "",
    contact_number: "",
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [submitting, setSubmitting] = useState(false)
  const [pendingPoFiles, setPendingPoFiles] = useState<File[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [stepValidation, setStepValidation] = useState({
    1: false,
    2: false,
    3: false,
  })
  const [selectedState, setSelectedState] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedDistrict, setSelectedDistrict] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [visibleSiteInfoFields, setVisibleSiteInfoFields] = useState(1)

  const normalizePoFiles = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string" && !!item)
    }

    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed)) {
          return parsed.filter((item): item is string => typeof item === "string" && !!item)
        }
      } catch {
        return []
      }
    }

    return []
  }

  const steps = [
    {
      number: 1,
      title: "Site Info",
      description: "Identification, location, and additional details",
      icon: LuInfo,
    },
    {
      number: 2,
      title: "Team & Contact",
      description: "Team members and contact",
      icon: LuUser,
    },
    {
      number: 3,
      title: "Purchase Order",
      description: "PO and PR details",
      icon: LuFileText,
    },
  ]

  // Fetch site data
  useEffect(() => {
    const fetchSiteData = async () => {
      if (!id) return

      setLoading(true)
      try {
        const response = await axiosInstance.get(`/survey/sites/${id}`)
        if (response.data.success) {
          const site = response.data.data.site

          // Helper to format ISO date string to YYYY-MM-DD for date inputs
          const formatDateForInput = (dateString: string | null | undefined): string => {
            if (!dateString) return ""
            try {
              const date = new Date(dateString)
              if (isNaN(date.getTime())) return ""
              return date.toISOString().split("T")[0]
            } catch {
              return ""
            }
          }

          setFormData({
            site_id: site.site_id || "",
            project_id: site.project_id || 0,
            activity_id: site.activity_id || undefined,
            reference_number: site.reference_number || "",
            client_allocation_date: formatDateForInput(site.client_allocation_date),
            site_name: site.site_name || "",
            site_address: site.site_address || "",
            opco_name: site.opco_name || "",
            opco_id: site.opco_id || "",
            state_circle_id: site.state_circle_id || undefined,
            district_id: site.district_id || undefined,
            pincode: site.pincode ? String(site.pincode) : "",
            site_latitude: site.site_latitude ? parseFloat(site.site_latitude) : undefined,
            site_longitude: site.site_longitude ? parseFloat(site.site_longitude) : undefined,
            site_priority: site.site_priority || "medium",
            pr_number: site.pr_number || "",
            pr_date: formatDateForInput(site.pr_date),
            po_number: site.po_number || "",
            po_date: formatDateForInput(site.po_date),
            po_files: normalizePoFiles(site.po_files),
            is_po_no_shared_at_allocation: site.is_po_no_shared_at_allocation || false,
            technician_name: site.technician_name || "",
            technician_number: site.technician_number || "",
            fse_name: site.fse_name || "",
            fse_number: site.fse_number || "",
            aom_name: site.aom_name || "",
            aom_number: site.aom_number || "",
            client_project_name: site.client_project_name || "",
            customer_requirement: site.customer_requirement || "",
            site_information1: site.site_information1 || "",
            site_information2: site.site_information2 || "",
            site_information3: site.site_information3 || "",
            contact_person: site.contact_person || "",
            contact_number: site.contact_number || "",
          })

          // Set state and district
          if (site.state_circle) {
            setSelectedState({
              id: site.state_circle.id,
              label: site.state_circle.state_name,
              value: site.state_circle.id
            })
          }

          if (site.district) {
            setSelectedDistrict({
              id: site.district.id,
              label: site.district.name,
              value: site.district.id
            })
          }

          // Determine visible site info fields
          if (site.site_information3) setVisibleSiteInfoFields(3)
          else if (site.site_information2) setVisibleSiteInfoFields(2)
          else setVisibleSiteInfoFields(1)

          // Mark steps as valid since data is loaded (validation will run on user interaction)
          // Step 1 is valid if site_id exists and meets criteria
          const siteIdValue = site.site_id || ''
          const step1IsValid = siteIdValue.length >= 3 && /^[A-Z0-9_-]+$/i.test(siteIdValue)
          setStepValidation({
            1: step1IsValid,
            2: true, // Step 2 has no required fields
            3: true, // Step 3 has no required fields
          })
          setErrors({}) // Clear any errors since data is loaded
          setPendingPoFiles([])
        }
      } catch (error) {
        console.error('Error fetching site data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchSiteData()
  }, [id])

  // Fetch projects and activities
  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects/list', {
        params: { limit: 100, status: 'ACTIVE' }
      })

      if (response.data.success) {
        setProjects(response.data.data.projects || [])
      }
    } catch (error) {
      console.error('Error fetching projects:', error)
    }
  }

  const fetchActivities = async () => {
    try {
      const response = await axiosInstance.get('/activities/list', {
        params: { limit: 100, status: 'ACTIVE' }
      })

      if (response.data.success) {
        setActivities(response.data.data.activities || [])
      }
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  useEffect(() => {
    fetchProjects()
    fetchActivities()
  }, [])

  const activeProjects = projects.filter((project) => project.status === "ACTIVE")
  const activeActivities = activities.filter((activity) => activity.status === "ACTIVE")
  const selectedProject = activeProjects.find((p) => p.id === formData.project_id)

  // Update form data
  const updateFormData = (field: keyof EditSiteData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }

    // Revalidate current step
    setTimeout(validateCurrentStep, 0)
  }

  const getDisplayFileName = (value: string) => {
    try {
      const cleanedValue = value.split("?")[0]
      const fileName = cleanedValue.split("/").pop()
      return fileName ? decodeURIComponent(fileName) : value
    } catch {
      return value
    }
  }

  const handlePoFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])
    if (!selectedFiles.length) {
      return
    }

    setPendingPoFiles((prev) => [...prev, ...selectedFiles])
    event.target.value = ""
  }

  const removePendingPoFile = (index: number) => {
    setPendingPoFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))
  }

  const removeExistingPoFile = (index: number) => {
    const currentPoFiles = formData.po_files || []
    updateFormData(
      "po_files",
      currentPoFiles.filter((_, fileIndex) => fileIndex !== index)
    )
  }

  const updateSite = async (siteData: EditSiteData) => {
    try {
      const response = await axiosInstance.put(`/survey/sites/${id}/update`, siteData)
      if (response.data.success) {
        console.log('Site updated successfully')
        navigate(`/sites/${id}`)
      } else {
        console.error('Failed to update site:', response.data.message)
      }
    } catch (error) {
      console.error('Error updating site:', error)
    }
  }

  // Validate specific step
  const validateStep = (step: number) => {
    const newErrors: { [key: string]: string } = {}
    let isValid = true

    switch (step) {
      case 1:
        const siteId = typeof formData.site_id === 'string' ? formData.site_id.trim() : ''
        if (!siteId) {
          newErrors.site_id = "Site ID is required"
          isValid = false
        } else if (siteId.length < 3) {
          newErrors.site_id = "Site ID must be at least 3 characters"
          isValid = false
        } else if (!/^[A-Z0-9_-]+$/i.test(siteId)) {
          newErrors.site_id = "Site ID must contain only alphanumeric characters, hyphens, and underscores"
          isValid = false
        }

        if (formData.site_latitude !== undefined && formData.site_latitude !== null) {
          const lat = Number(formData.site_latitude)
          if (isNaN(lat) || lat < 6 || lat > 38) {
            newErrors.site_latitude = "Latitude must be within India range (6 to 38)"
            isValid = false
          }
        }

        if (formData.site_longitude !== undefined && formData.site_longitude !== null) {
          const lng = Number(formData.site_longitude)
          if (isNaN(lng) || lng < 68 || lng > 98) {
            newErrors.site_longitude = "Longitude must be within India range (68 to 98)"
            isValid = false
          }
        }

        const siteName = typeof formData.site_name === 'string' ? formData.site_name.trim() : ''
        if (siteName && siteName.length < 2) {
          newErrors.site_name = "Site name must be at least 2 characters"
          isValid = false
        }

        const siteAddress = typeof formData.site_address === 'string' ? formData.site_address.trim() : ''
        if (siteAddress && siteAddress.length < 5) {
          newErrors.site_address = "Site address must be at least 5 characters"
          isValid = false
        }
        break

      case 2:
        // Step 2 has no required validations - all fields are optional
        break

      case 3:
        // Step 3 has no required validations - all fields are optional
        break
    }

    setErrors((prev) => ({ ...prev, ...newErrors }))
    setStepValidation((prev) => ({ ...prev, [step]: isValid }))
    return isValid
  }

  // Validate current step
  const validateCurrentStep = () => {
    return validateStep(currentStep)
  }

  // Handle step navigation
  const goToStep = (step: number) => {
    // When moving forward, validate current step first
    if (step > currentStep) {
      const isCurrentValid = validateCurrentStep()
      if (!isCurrentValid) {
        return // Block navigation if current step has errors
      }
    }

    if (step >= 1 && step <= 3) {
      setCurrentStep(step)
    }
  }

  // Handle next button
  const handleNext = () => {
    const isValid = validateCurrentStep()
    if (!isValid) {
      return // Block navigation if validation fails
    }
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  // Handle previous button
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    // Validate all steps
    const step1Valid = validateStep(1)
    const step2Valid = validateStep(2)
    const step3Valid = validateStep(3)

    if (!step1Valid || !step2Valid || !step3Valid) {
      return
    }

    setSubmitting(true)
    try {
      const uploadedPoFiles = pendingPoFiles.length
        ? await uploadFilesWithPresignedUrls(pendingPoFiles)
        : []
      const poFiles = Array.from(new Set([...(formData.po_files || []), ...uploadedPoFiles]))

      const safeTrim = (value: string | undefined | null): string | undefined => {
        if (typeof value === 'string') {
          const trimmed = value.trim()
          return trimmed || undefined
        }
        return undefined
      }

      const submitData: EditSiteData = {
        ...formData,
        site_id: typeof formData.site_id === 'string' ? formData.site_id.trim() : '',
        activity_id: formData.activity_id || undefined,
        reference_number: safeTrim(formData.reference_number),
        site_name: safeTrim(formData.site_name),
        site_address: safeTrim(formData.site_address),
        opco_name: safeTrim(formData.opco_name),
        opco_id: safeTrim(formData.opco_id),
        state_circle_id: formData.state_circle_id || undefined,
        district_id: formData.district_id || undefined,
        pincode: safeTrim(formData.pincode),
        pr_number: safeTrim(formData.pr_number),
        po_number: safeTrim(formData.po_number),
        technician_name: safeTrim(formData.technician_name),
        technician_number: safeTrim(formData.technician_number),
        fse_name: safeTrim(formData.fse_name),
        fse_number: safeTrim(formData.fse_number),
        aom_name: safeTrim(formData.aom_name),
        aom_number: safeTrim(formData.aom_number),
        client_project_name: safeTrim(formData.client_project_name),
        customer_requirement: safeTrim(formData.customer_requirement),
        site_information1: safeTrim(formData.site_information1),
        site_information2: safeTrim(formData.site_information2),
        site_information3: safeTrim(formData.site_information3),
        contact_person: safeTrim(formData.contact_person),
        contact_number: safeTrim(formData.contact_number),
        site_latitude: formData.site_latitude || undefined,
        site_longitude: formData.site_longitude || undefined,
        po_files: poFiles,
      }

      await updateSite(submitData)
    } catch (error) {
      console.error("Failed to update site:", error)
    } finally {
      setSubmitting(false)
    }
  }

  // Check if step is accessible
  const isStepAccessible = (step: number) => {
    if (step === 1) return true
    if (step === 2) return stepValidation[1]
    if (step === 3) return stepValidation[1] && stepValidation[2]
    return false
  }

  // Handle state selection
  const handleStateChange = (option: { id: number; label: string; value: string | number } | null) => {
    setSelectedState(option)
    setSelectedDistrict(null) // Clear district when state changes
    updateFormData("state_circle_id", option ? option.id : undefined)
    updateFormData("district_id", undefined)
  }

  // Handle district selection
  const handleDistrictChange = (option: { id: number; label: string; value: string | number } | null) => {
    setSelectedDistrict(option)
    updateFormData("district_id", option ? option.id : undefined)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LuLoader className="w-12 h-12 animate-spin text-blue-800 mx-auto mb-4" />
          <p className="text-gray-600">Loading site data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white p-4 rounded border">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 flex-shrink-0">
        <div className="min-w-0 flex-1 mb-2">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 truncate">Edit Site</h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
            Update site details and project information
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between w-full bg-white border-b border-gray-200 px-2 sm:px-6 py-3 sm:py-4 overflow-x-auto">
        <div className="flex items-center gap-6 min-w-max mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon
            const isActive = currentStep === step.number
            const isCompleted = stepValidation[step.number as 1 | 2 | 3]
            const isAccessible = isStepAccessible(step.number)

            return (
              <div key={step.number} className="flex items-center">
                {/* Step Circle */}
                <button
                  onClick={() => goToStep(step.number)}
                  disabled={!isAccessible}
                  className={`relative flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 flex-shrink-0
            ${isActive
                      ? "border-blue-800 bg-blue-800 text-white"
                      : isCompleted
                        ? "border-green-600 bg-green-600 text-white"
                        : isAccessible
                          ? "border-gray-300 bg-white text-gray-600 hover:border-gray-400"
                          : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                >
                  {isCompleted && !isActive ? (
                    <LuCheck className="w-5 h-5" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </button>

                {/* Step Info */}
                <div className="ml-3 hidden md:block">
                  <p
                    className={`text-sm font-medium whitespace-nowrap ${isActive
                        ? "text-blue-800"
                        : isCompleted
                          ? "text-green-600"
                          : isAccessible
                            ? "text-gray-900"
                            : "text-gray-400"
                      }`}
                  >
                    {step.title}
                  </p>
                  <p
                    className={`text-xs whitespace-nowrap ${isActive
                        ? "text-blue-500"
                        : isCompleted
                          ? "text-green-500"
                          : isAccessible
                            ? "text-gray-500"
                            : "text-gray-400"
                      }`}
                  >
                    {step.description}
                  </p>
                </div>

                {/* Mobile title */}
                <div className="ml-2 md:hidden">
                  <p
                    className={`text-xs font-medium whitespace-nowrap ${isActive
                        ? "text-blue-800"
                        : isCompleted
                          ? "text-green-600"
                          : isAccessible
                            ? "text-gray-900"
                            : "text-gray-400"
                      }`}
                  >
                    {step.title}
                  </p>
                </div>

                {/* Connector */}
                {index < steps.length - 1 && (
                  <div
                    className={`w-10 sm:w-20 h-0.5 mx-3 ${stepValidation[step.number as 1 | 2 | 3]
                        ? "bg-green-300"
                        : "bg-gray-200"
                      }`}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Step 1: Site Info */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* Basic Site Information */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuInfo className="w-4 sm:w-5 h-4 sm:h-5 text-blue-800" />
                Basic Site Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site ID *</label>
                  <input
                    type="text"
                    value={formData.site_id}
                    onChange={(e) => updateFormData("site_id", e.target.value)}
                    placeholder="Enter unique site identifier (e.g., SITE001)"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_id ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                      }`}
                    disabled={submitting}
                  />
                  {errors.site_id && <p className="mt-1 text-xs text-red-600">{errors.site_id}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                    <SelectWithSearch
                      value={activeActivities.find(a => a.id === formData.activity_id) ? { id: formData.activity_id!, label: activeActivities.find(a => a.id === formData.activity_id)!.activity_name, value: formData.activity_id! } : null}
                      onChange={(option) => updateFormData("activity_id", option ? option.id : undefined)}
                      options={activeActivities.map(a => ({ id: a.id, label: a.activity_name, value: a.id }))}
                      placeholder="Select an activity"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
                    <input
                      type="text"
                      value={formData.reference_number || ""}
                      onChange={(e) => updateFormData("reference_number", e.target.value)}
                      placeholder="Enter reference number"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client Allocation Date</label>
                  <input
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    value={formData.client_allocation_date || ""}
                    onChange={(e) => updateFormData("client_allocation_date", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.client_allocation_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                      }`}
                    disabled={submitting}
                  />
                  {errors.client_allocation_date && <p className="mt-1 text-xs text-red-600">{errors.client_allocation_date}</p>}
                </div>
              </div>
            </div>

            {/* Project Assignment */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuNetwork className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                Project Assignment
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Project *</label>
                <SelectWithSearch
                  value={activeProjects.find(p => p.id === formData.project_id) ? { id: formData.project_id, label: activeProjects.find(p => p.id === formData.project_id)!.project_name, value: formData.project_id } : null}
                  onChange={(option) => updateFormData("project_id", option ? option.id : 0)}
                  options={activeProjects.map(p => ({ id: p.id, label: p.project_name, value: p.id }))}
                  placeholder="Select a project"
                  disabled={submitting}
                />
                {errors.project_id && <p className="mt-1 text-xs text-red-600">{errors.project_id}</p>}

                {activeProjects.length === 0 && (
                  <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                    <LuInfo className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">No active projects available</p>
                      <p className="text-xs mt-1">Please ensure there are active projects before updating.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Project Info */}
            {selectedProject && (
              <div className="bg-blue-50 border border-blue-200 rounded p-4 sm:p-6">
                <h4 className="text-base sm:text-lg font-semibold text-blue-800 mb-4">Selected Project Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Project Name</label>
                    <p className="text-sm text-blue-900">{selectedProject.project_name}</p>
                  </div>
                  {selectedProject.client && (
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Client</label>
                      <p className="text-sm text-blue-900">{selectedProject.client.client_name}</p>
                    </div>
                  )}
                  {selectedProject.activity && (
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Activity</label>
                      <p className="text-sm text-blue-900">{selectedProject.activity.activity_name}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Status</label>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {selectedProject.status}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Location Details */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuMapPin className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                Site Location
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                    <input
                      type="text"
                      value={formData.site_name || ""}
                      onChange={(e) => updateFormData("site_name", e.target.value)}
                      placeholder="Enter site name"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_name ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.site_name && <p className="mt-1 text-xs text-red-600">{errors.site_name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Priority</label>
                    <select
                      value={formData.site_priority || "medium"}
                      onChange={(e) => updateFormData("site_priority", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Address</label>
                  <textarea
                    value={formData.site_address || ""}
                    onChange={(e) => updateFormData("site_address", e.target.value)}
                    placeholder="Enter complete site address"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical ${errors.site_address ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                      }`}
                    disabled={submitting}
                  />
                  {errors.site_address && <p className="mt-1 text-xs text-red-600">{errors.site_address}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <SelectWithSearch
                      value={selectedState}
                      onChange={handleStateChange}
                      placeholder="Select state"
                      disabled={submitting}
                      apiEndpoint="/state-circle/list"
                      dataKey="states"
                      labelKey="state_name"
                      displayFormat={(item) => `${item.state_name}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                    <SelectWithSearch
                      value={selectedDistrict}
                      onChange={handleDistrictChange}
                      placeholder="Select district"
                      disabled={submitting || !selectedState}
                      apiEndpoint="/state-circle"
                      dataKey="districts"
                      dependencyId={selectedState?.id}
                      labelKey="name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                    <input
                      type="text"
                      value={formData?.pincode || ""}
                      onChange={(e) => updateFormData("pincode", e.target.value)}
                      placeholder="Enter pincode"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* OpCo Details */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuBuilding className="w-4 sm:w-5 h-4 sm:h-5 text-orange-600" />
                Operating Company Details
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OpCo Name</label>
                  <input
                    type="text"
                    value={formData.opco_name || ""}
                    onChange={(e) => updateFormData("opco_name", e.target.value)}
                    placeholder="Enter operating company name"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OpCo ID</label>
                  <input
                    type="text"
                    value={formData.opco_id || ""}
                    onChange={(e) => updateFormData("opco_id", e.target.value)}
                    placeholder="Enter operating company ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            {/* Coordinates */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuGlobe className="w-4 sm:w-5 h-4 sm:h-5 text-blue-800" />
                Site GPS Coordinates
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.site_latitude || ""}
                    onChange={(e) => updateFormData("site_latitude", e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="Enter latitude (e.g., 28.6139)"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_latitude ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                      }`}
                    disabled={submitting}
                  />
                  {errors.site_latitude && <p className="mt-1 text-xs text-red-600">{errors.site_latitude}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.site_longitude || ""}
                    onChange={(e) => updateFormData("site_longitude", e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="Enter longitude (e.g., 77.2090)"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_longitude ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                      }`}
                    disabled={submitting}
                  />
                  {errors.site_longitude && <p className="mt-1 text-xs text-red-600">{errors.site_longitude}</p>}
                </div>
              </div>

              <div className="mt-3 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                <LuInfo className="w-4 h-4 text-blue-800 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p>GPS coordinates help in precise site location identification and navigation.</p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuClipboard className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-600" />
                Additional Site Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer Requirement</label>
                  <textarea
                    value={formData.customer_requirement || ""}
                    onChange={(e) => updateFormData("customer_requirement", e.target.value)}
                    placeholder="Enter customer requirements"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 1</label>
                  <textarea
                    value={formData.site_information1 || ""}
                    onChange={(e) => updateFormData("site_information1", e.target.value)}
                    placeholder="Enter additional site information"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                    disabled={submitting}
                  />
                </div>

                {visibleSiteInfoFields >= 2 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 2</label>
                    <textarea
                      value={formData.site_information2 || ""}
                      onChange={(e) => updateFormData("site_information2", e.target.value)}
                      placeholder="Enter additional site information"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      disabled={submitting}
                    />
                  </div>
                )}

                {visibleSiteInfoFields >= 3 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 3</label>
                    <textarea
                      value={formData.site_information3 || ""}
                      onChange={(e) => updateFormData("site_information3", e.target.value)}
                      placeholder="Enter additional site information"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      disabled={submitting}
                    />
                  </div>
                )}

                {visibleSiteInfoFields < 3 && (
                  <div className="flex justify-start">
                    <button
                      type="button"
                      onClick={() => setVisibleSiteInfoFields(prev => prev + 1)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white text-sm font-medium rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      <LuPlus className="w-4 h-4" />
                      Add More
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Team & Contact */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Team Members */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuUser className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                Team Assignment
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Technician Name</label>
                    <input
                      type="text"
                      value={formData.technician_name || ""}
                      onChange={(e) => updateFormData("technician_name", e.target.value)}
                      placeholder="Enter technician name"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Technician Number</label>
                    <input
                      type="tel"
                      value={formData.technician_number || ""}
                      onChange={(e) => updateFormData("technician_number", e.target.value)}
                      placeholder="Enter technician contact number"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">FSE Name</label>
                    <input
                      type="text"
                      value={formData.fse_name || ""}
                      onChange={(e) => updateFormData("fse_name", e.target.value)}
                      placeholder="Enter FSE name"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">FSE Number</label>
                    <input
                      type="tel"
                      value={formData.fse_number || ""}
                      onChange={(e) => updateFormData("fse_number", e.target.value)}
                      placeholder="Enter FSE contact number"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AOM Name</label>
                    <input
                      type="text"
                      value={formData.aom_name || ""}
                      onChange={(e) => updateFormData("aom_name", e.target.value)}
                      placeholder="Enter AOM name"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AOM Number</label>
                    <input
                      type="tel"
                      value={formData.aom_number || ""}
                      onChange={(e) => updateFormData("aom_number", e.target.value)}
                      placeholder="Enter AOM contact number"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuPhone className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                Site Owner Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                  <input
                    type="text"
                    value={formData.contact_person || ""}
                    onChange={(e) => updateFormData("contact_person", e.target.value)}
                    placeholder="Enter contact person name"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                  <input
                    type="tel"
                    value={formData.contact_number || ""}
                    onChange={(e) => updateFormData("contact_number", e.target.value)}
                    placeholder="Enter contact number"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Purchase Order Details */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Purchase Request */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuFileText className="w-4 sm:w-5 h-4 sm:h-5 text-blue-800" />
                Purchase Request Details
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PR Number</label>
                  <input
                    type="text"
                    value={formData.pr_number || ""}
                    onChange={(e) => updateFormData("pr_number", e.target.value)}
                    placeholder="Enter PR number"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PR Date</label>
                  <input
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    value={formData.pr_date || ""}
                    onChange={(e) => updateFormData("pr_date", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.pr_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                      }`}
                    disabled={submitting}
                  />
                  {errors.pr_date && <p className="mt-1 text-xs text-red-600">{errors.pr_date}</p>}
                </div>
              </div>
            </div>

            {/* Purchase Order */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LuClipboard className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                Purchase Order Details
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PO Number</label>
                    <input
                      type="text"
                      value={formData.po_number || ""}
                      onChange={(e) => updateFormData("po_number", e.target.value)}
                      placeholder="Enter PO number"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PO Date</label>
                    <input
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formData.po_date || ""}
                      onChange={(e) => updateFormData("po_date", e.target.value)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.po_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.po_date && <p className="mt-1 text-xs text-red-600">{errors.po_date}</p>}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_po_no_shared_at_allocation"
                    checked={formData.is_po_no_shared_at_allocation || false}
                    onChange={(e) => updateFormData("is_po_no_shared_at_allocation", e.target.checked)}
                    className="w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    disabled={submitting}
                  />
                  <label htmlFor="is_po_no_shared_at_allocation" className="text-sm font-medium text-gray-700">
                    PO number shared at allocation
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PO Files</label>
                  <input
                    type="file"
                    multiple
                    onChange={handlePoFilesChange}
                    className="w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-800 hover:file:bg-blue-100"
                    disabled={submitting}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Files are uploaded using presigned URLs when you submit this form.
                  </p>
                </div>

                {(formData.po_files?.length || 0) > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Existing PO Files</p>
                    <div className="space-y-2">
                      {(formData.po_files || []).map((fileUrl, index) => (
                        <div key={`${fileUrl}-${index}`} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-700 hover:underline truncate pr-3"
                          >
                            {getDisplayFileName(fileUrl)}
                          </a>
                          <button
                            type="button"
                            onClick={() => removeExistingPoFile(index)}
                            className="text-xs text-red-600 hover:text-red-700"
                            disabled={submitting}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pendingPoFiles.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">New Files To Upload</p>
                    <div className="space-y-2">
                      {pendingPoFiles.map((file, index) => (
                        <div key={`${file.name}-${file.size}-${index}`} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                          <span className="text-sm text-gray-700 truncate pr-3">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removePendingPoFile(index)}
                            className="text-xs text-red-600 hover:text-red-700"
                            disabled={submitting}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer - Fixed */}
      <div className="border-t border-gray-200 bg-white p-4 sm:p-6 flex-shrink-0">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Progress indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-600 order-2 sm:order-1">
            <span>Step {currentStep} of {steps.length}</span>
            <div className="w-24 bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-blue-800 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
              >
                Previous
              </button>
            )}

            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={submitting || !stepValidation[currentStep as 1 | 2 | 3]}
                className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
              >
                Next
                <LuChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || !Object.values(stepValidation).slice(0, 3).every(Boolean)}
                className="flex items-center gap-2 px-6 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
              >
                {submitting ? (
                  <>
                    <LuLoader className="w-4 h-4 animate-spin" />
                    Updating Site...
                  </>
                ) : (
                  <>
                    <LuSave className="w-4 h-4" />
                    Update Site
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditSite
